// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Ui from "../../shared/Ui.re.mjs";
import * as React from "react";
import * as HomeAtoms from "../../atoms/home/HomeAtoms.re.mjs";
import * as HomeContent from "./HomeContent.re.mjs";
import * as LucideReact from "lucide-react";
import * as Core from "@linaria/core";
import * as HomeMolecules from "../../molecules/home/HomeMolecules.re.mjs";
import * as Stdlib_Option from "@rescript/runtime/lib/es6/Stdlib_Option.js";
import * as FramerMotion from "framer-motion";
import * as JsxRuntime from "react/jsx-runtime";

import { css, cx } from '@linaria/core'
;

import { t } from '@lingui/macro'
;

let defaultSku = "custphotoreal";

function products() {
  return [
    {
      sku: "custphotoreal",
      label: t`Oil Painting`,
      medium: t`Oil on canvas`,
      title: t`Custom Oil Painting`,
      price: 107,
      description: t`100% free-hand painted onto canvas by a master artist — rich, luminous oils with real depth and texture. Work directly with the artist to customize, or just upload a photo and let them handle it. Available rolled, gallery wrapped, or framed.`,
      bullets: [
        t`Photo-realism — oil gets the closest to looking exactly like your photo.`,
        t`Portraits, landscapes, even abstract art — oil is suitable for all.`,
        t`Cleaning up or restoring old photos.`,
        t`Customization — artists can paint in or remove objects from your photo.`
      ]
    },
    {
      sku: "custwatercolor",
      label: t`Watercolor`,
      medium: t`Watercolor on paper`,
      title: t`Custom Watercolor Painting`,
      price: 107,
      description: t`100% free-hand painted onto premium watercolor paper by a master artist — delicate, transparent layers that capture light, emotion, and dreamy atmospheres. Work directly with the artist to customize, or just upload a photo and let them handle it. Available rolled or framed.`,
      bullets: [
        t`An obviously hand-painted, soft and ethereal look.`,
        t`Cleaning up or restoring old or poor quality photos.`,
        t`Customization — artists can paint in or remove objects from your photo.`
      ]
    },
    {
      sku: "custcharcoal",
      label: t`Charcoal`,
      medium: t`Charcoal on paper`,
      title: t`Custom Charcoal Drawing`,
      price: 95,
      description: t`100% free-hand drawn on art paper by a master artist — dramatic, timeless black-and-white with rich contrast and expressive strokes. Work directly with the artist to customize, or just upload a photo and let them handle it. Available rolled or framed.`,
      bullets: [
        t`Portraits — an artsy, expressive look with dramatic light and contrast.`,
        t`Cleaning up or restoring old or poor quality photos.`,
        t`Customization — artists can paint in or remove objects from your photo.`
      ]
    },
    {
      sku: "custpencil",
      label: t`Pencil`,
      medium: t`Pencil on paper`,
      title: t`Custom Pencil Drawing`,
      price: 95,
      description: t`100% free-hand drawn on art paper by a master artist — fine graphite with delicate shading and detail. Work directly with the artist to customize, or just upload a photo and let them handle it. Available rolled or framed.`,
      bullets: [
        t`Portraits — an artsy, hand-drawn pencil look for your subject.`,
        t`Cleaning up or restoring old or poor quality photos.`,
        t`Customization — artists can paint in or remove objects from your photo.`
      ]
    },
    {
      sku: "custcoloredpencil",
      label: t`Colored Pencil`,
      medium: t`Colored pencil on paper`,
      title: t`Custom Colored Pencil`,
      price: 107,
      description: t`100% free-hand drawn on art paper by a master artist — vibrant, layered color with incredible precision and detail. Work directly with the artist to customize, or just upload a photo and let them handle it. Available rolled or framed.`,
      bullets: [
        t`Portraits & landscapes — vibrant, saturated color with extreme detail.`,
        t`Cleaning up or restoring old or poor quality photos.`,
        t`Customization — artists can paint in or remove objects from your photo.`
      ]
    },
    {
      sku: "digital-portrait-painting-canvas",
      label: t`Digital Portrait`,
      medium: t`Digital giclée on canvas`,
      title: t`Custom Digital Portrait`,
      price: 107,
      description: t`Hand-painted digitally by a master artist in unique artistic styles, then printed on archival giclée canvas at a microscopic 300 DPI — endlessly versatile and shipped gallery wrapped, ready to hang.`,
      bullets: [
        t`Accuracy — traced over your original photo for maximum likeness.`,
        t`Affordability — costs less at bigger sizes, since it prints at any scale.`,
        t`Versatility — artists can even simulate traditional mediums like oil or watercolor.`,
        t`Customization — painted on the computer, so changes are easy to make.`
      ]
    }
  ];
}

function samplesFor(all, sku) {
  return Stdlib_Option.mapOr(all.find(entry => entry.sku === sku), [], entry => entry.samples);
}

function ChooseMediumSection(props) {
  let products$1 = products().filter(p => !HomeContent.isHidden(p.sku));
  let match = React.useState(() => defaultSku);
  let setActiveSku = match[1];
  let activeSku = match[0];
  let activeProduct = Stdlib_Option.getOr(products$1.find(p => p.sku === activeSku), products$1[0]);
  let activeSamples = samplesFor(props.samples, activeProduct.sku);
  let totalCount = products$1.length;
  let total = HomeAtoms.pad(totalCount);
  let label = activeProduct.label;
  let price = activeProduct.price.toString();
  return JsxRuntime.jsx("section", {
    children: JsxRuntime.jsxs("div", {
      children: [
        JsxRuntime.jsxs("div", {
          children: [
            JsxRuntime.jsx(HomeAtoms.Eyebrow.make, {
              children: t`Our Products — ${total} Mediums`,
              className: Core.cx("mb-6")
            }),
            JsxRuntime.jsx(HomeAtoms.SerifHeading.make, {
              children: t`Choose Your Medium`,
              className: Core.cx("text-4xl md:text-5xl lg:text-6xl mb-6")
            }),
            JsxRuntime.jsx("p", {
              children: t`We offer custom oil paintings on canvas, custom watercolor paintings on paper, custom pencil drawings on paper, and more. Each piece is made by hand, by a real artist.`,
              className: Core.cx("max-w-xl mx-auto text-gray-600 font-light leading-relaxed")
            }),
            JsxRuntime.jsxs("p", {
              children: [
                JsxRuntime.jsx("span", {
                  children: "\u{1F1FA}\u{1F1F8}",
                  className: Core.cx("mr-1")
                }),
                t`Questions? Call`,
                " ",
                JsxRuntime.jsx("a", {
                  children: "1-844-333-3729",
                  className: Core.cx("text-gray-800 hover:text-gray-500 transition-colors underline-offset-4 hover:underline"),
                  href: "tel:18443333729"
                }),
                JsxRuntime.jsx("span", {
                  children: t`(10:00 AM – 6:00 PM EST, M–F)`,
                  className: Core.cx("text-gray-500 ml-1")
                })
              ],
              className: Core.cx("mt-8 text-xs tracking-wide text-gray-500 font-light")
            })
          ],
          className: Core.cx("text-center mb-20 md:mb-24")
        }),
        JsxRuntime.jsx("div", {
          children: JsxRuntime.jsx("div", {
            children: products$1.map((product, idx) => {
              let isActive = activeProduct.sku === product.sku;
              return JsxRuntime.jsxs("button", {
                children: [
                  JsxRuntime.jsx("span", {
                    children: HomeAtoms.pad(idx + 1 | 0) + ` / ` + total,
                    className: Core.cx("block text-[10px] tracking-[0.25em] uppercase font-light mb-2 transition-colors duration-300", isActive ? "text-gray-900" : "text-gray-400")
                  }),
                  JsxRuntime.jsx("span", {
                    children: product.label,
                    className: Core.cx("block font-serif font-light text-lg md:text-xl transition-colors duration-300", isActive ? "text-gray-900" : "text-gray-400 group-hover:text-gray-700")
                  }),
                  JsxRuntime.jsx("span", {
                    className: Core.cx("block mt-2 h-px transition-all duration-500", isActive ? "w-12 bg-gray-900" : "w-0 bg-transparent group-hover:w-6 group-hover:bg-gray-300")
                  })
                ],
                "aria-selected": isActive,
                className: Core.cx("text-left group"),
                role: "tab",
                type: "button",
                onClick: param => setActiveSku(param => product.sku)
              }, product.sku);
            }),
            className: Core.cx("grid grid-cols-2 md:grid-cols-3 gap-y-8 gap-x-6"),
            role: "tablist"
          }),
          className: Core.cx("border-t border-gray-200 pt-8 mb-16 md:mb-20")
        }),
        JsxRuntime.jsx(FramerMotion.AnimatePresence, {
          mode: "wait",
          children: JsxRuntime.jsxs(FramerMotion.motion.div, {
            animate: {
              opacity: 1.0,
              y: 0.0
            },
            initial: {
              opacity: 0.0,
              y: 8.0
            },
            exit: {
              opacity: 0.0,
              y: -4.0
            },
            transition: {
              duration: 0.4,
              ease: "easeOut"
            },
            children: [
              JsxRuntime.jsxs("div", {
                children: [
                  JsxRuntime.jsxs("div", {
                    children: [
                      JsxRuntime.jsx(HomeAtoms.SerifHeading.make, {
                        level: 3,
                        children: activeProduct.title,
                        className: Core.cx("text-3xl md:text-4xl lg:text-5xl mb-8 leading-[1.1]")
                      }),
                      JsxRuntime.jsx("p", {
                        children: activeProduct.description,
                        className: Core.cx("text-gray-600 font-light leading-relaxed mb-10 max-w-md")
                      }),
                      JsxRuntime.jsxs("div", {
                        children: [
                          JsxRuntime.jsx("span", {
                            children: t`$${price}`,
                            className: Core.cx("font-serif font-light text-2xl text-gray-900")
                          }),
                          JsxRuntime.jsx("span", {
                            children: t`Starting price`,
                            className: Core.cx("text-xs tracking-widest uppercase text-gray-500 font-light")
                          })
                        ],
                        className: Core.cx("flex items-baseline gap-6 mb-2")
                      }),
                      JsxRuntime.jsx("p", {
                        children: t`Just 20% deposit to start.`,
                        className: Core.cx("text-xs text-gray-500 font-light italic mb-10")
                      }),
                      JsxRuntime.jsx(HomeAtoms.ArrowLink.make, {
                        href: HomeContent.productLink(activeProduct.sku),
                        label: t`View Pricing & Place Order`
                      })
                    ],
                    className: Core.cx("md:col-span-6")
                  }),
                  JsxRuntime.jsxs("div", {
                    children: [
                      JsxRuntime.jsx(HomeAtoms.Eyebrow.make, {
                        children: t`Recommended For`,
                        className: Core.cx("mb-8")
                      }),
                      JsxRuntime.jsx("ul", {
                        children: activeProduct.bullets.map((bullet, idx) => JsxRuntime.jsx(HomeMolecules.IndexedRow.make, {
                          index: idx,
                          children: bullet
                        }, idx.toString()))
                      })
                    ],
                    className: Core.cx("md:col-span-6")
                  })
                ],
                className: Core.cx("grid grid-cols-1 md:grid-cols-12 gap-y-16 md:gap-x-16 lg:gap-x-24"),
                role: "tabpanel"
              }),
              JsxRuntime.jsxs("div", {
                children: [
                  JsxRuntime.jsxs("div", {
                    children: [
                      JsxRuntime.jsxs("div", {
                        children: [
                          JsxRuntime.jsx(HomeAtoms.Eyebrow.make, {
                            children: t`Featured Work`,
                            className: Core.cx("mb-2")
                          }),
                          JsxRuntime.jsx(HomeAtoms.SerifHeading.make, {
                            level: 4,
                            children: t`Recent ${label} orders`,
                            className: Core.cx("text-2xl md:text-3xl")
                          })
                        ]
                      }),
                      JsxRuntime.jsxs("a", {
                        children: [
                          t`View full gallery`,
                          JsxRuntime.jsx(LucideReact.ArrowRight, {
                            size: 12,
                            strokeWidth: 1.5
                          })
                        ],
                        className: Core.cx("hidden sm:inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-light text-gray-700 hover:text-gray-900 hover:gap-3 transition-all duration-300 whitespace-nowrap"),
                        href: HomeContent.galleryLink(activeProduct.sku)
                      })
                    ],
                    className: Core.cx("flex items-end justify-between mb-10 md:mb-12 border-b border-gray-200 pb-4")
                  }),
                  activeSamples.length === 0 ? JsxRuntime.jsx("p", {
                      children: t`Recent orders for this medium will appear here soon.`,
                      className: Core.cx("text-sm text-gray-500 font-light italic")
                    }) : JsxRuntime.jsx("div", {
                      children: activeSamples.map((sample, idx) => JsxRuntime.jsxs(FramerMotion.motion.figure, {
                        className: Core.cx("group"),
                        animate: {
                          opacity: 1.0,
                          y: 0.0
                        },
                        initial: {
                          opacity: 0.0,
                          y: 12.0
                        },
                        transition: {
                          duration: 0.5,
                          ease: "easeOut",
                          delay: 0.05 * idx
                        },
                        children: [
                          JsxRuntime.jsx("a", {
                            children: JsxRuntime.jsx("div", {
                              children: JsxRuntime.jsx(Ui.DImg.make, {
                                src: sample.src,
                                alt: sample.alt,
                                className: Core.cx("w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"),
                                imgixParams: HomeMolecules.portraitParams(),
                                sizes: "(min-width: 768px) 30vw, 50vw",
                                width: 480,
                                height: 600
                              }),
                              className: Core.cx("relative overflow-hidden bg-gray-100 aspect-[4/5]")
                            }),
                            className: Core.cx("block"),
                            href: sample.link
                          }),
                          JsxRuntime.jsxs("figcaption", {
                            children: [
                              JsxRuntime.jsx("span", {
                                children: HomeAtoms.pad(idx + 1 | 0),
                                className: Core.cx("text-[10px] tracking-widest text-gray-400 font-light tabular-nums")
                              }),
                              JsxRuntime.jsx("span", {
                                children: activeProduct.medium,
                                className: Core.cx("text-xs text-gray-600 font-light tracking-wide")
                              })
                            ],
                            className: Core.cx("mt-3 flex items-baseline gap-3")
                          })
                        ]
                      }, sample.id)),
                      className: Core.cx("grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6")
                    }),
                  JsxRuntime.jsxs("a", {
                    children: [
                      t`View full gallery`,
                      JsxRuntime.jsx(LucideReact.ArrowRight, {
                        size: 12,
                        strokeWidth: 1.5
                      })
                    ],
                    className: Core.cx("sm:hidden mt-10 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-light text-gray-700 hover:text-gray-900 hover:gap-3 transition-all duration-300"),
                    href: HomeContent.galleryLink(activeProduct.sku)
                  })
                ],
                className: Core.cx("mt-24 md:mt-32")
              })
            ]
          }, activeProduct.sku)
        }),
        JsxRuntime.jsx("p", {
          children: t`Secure checkout · Visa · Mastercard · Amex · Apple Pay · PayPal · Google Pay`,
          className: Core.cx("text-center mt-24 md:mt-32 text-[10px] tracking-[0.25em] uppercase text-gray-400 font-light")
        })
      ],
      className: Core.cx("max-w-6xl mx-auto")
    }),
    className: Core.cx("w-full bg-[#faf9f6] py-24 md:py-32 px-6 md:px-12"),
    id: "choose-medium"
  });
}

let make = ChooseMediumSection;

export {
  defaultSku,
  products,
  samplesFor,
  make,
}
/*  Not a pure module */
